import apiClient from '../client';
import {
  Notification,
  NotificationCreate,
  BulkNotificationCreate,
  NotificationCount,
  BulkNotificationOperation,
  BulkOperationResponse,
  Subscription,
  SubscriptionCreate,
  SubscriptionUpdate,
  AvailableSubscriptionsResponse,
  Preference,
  PreferenceCreate,
  UserOption,
  OptionCreate,
  OptionUpdate,
  GlobalOption,
  HealthResponse,
  NotificationQueryParams,
} from '../types/notification.types';

/**
 * Notification Service API
 * Base path: /api/notifications
 */
export const notificationsApi = {
  // ============================================================================
  // HEALTH CHECKS
  // ============================================================================

  /**
   * Basic health check
   * GET /api/notifications/health
   */
  health: async (): Promise<HealthResponse> => {
    const response = await apiClient.get<HealthResponse>(
      '/notifications/health'
    );
    return response.data;
  },

  /**
   * Liveness check
   * GET /api/notifications/health/live
   */
  healthLive: async (): Promise<HealthResponse> => {
    const response = await apiClient.get<HealthResponse>(
      '/notifications/health/live'
    );
    return response.data;
  },

  /**
   * Readiness check
   * GET /api/notifications/health/ready
   */
  healthReady: async (): Promise<HealthResponse> => {
    const response = await apiClient.get<HealthResponse>(
      '/notifications/health/ready'
    );
    return response.data;
  },

  // ============================================================================
  // NOTIFICATIONS
  // ============================================================================

  /**
   * Get list of notifications with optional filtering
   * GET /api/notifications/
   */
  getNotifications: async (
    params?: NotificationQueryParams
  ): Promise<Notification[]> => {
    const response = await apiClient.get<Notification[]>('/notifications/', {
      params,
    });
    return response.data;
  },

  /**
   * Get only unread notifications
   * GET /api/notifications/unread
   */
  getUnreadNotifications: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<Notification[]> => {
    const response = await apiClient.get<Notification[]>(
      '/notifications/unread',
      { params }
    );
    return response.data;
  },

  /**
   * Get notification counts by status
   * GET /api/notifications/count
   */
  getNotificationCount: async (): Promise<NotificationCount> => {
    const response = await apiClient.get<NotificationCount>(
      '/notifications/count'
    );
    return response.data;
  },

  /**
   * Get a single notification by ID
   * GET /api/notifications/id/{notification_id}
   */
  getNotificationById: async (id: string): Promise<Notification> => {
    const response = await apiClient.get<Notification>(
      `/notifications/id/${id}`
    );
    return response.data;
  },

  /**
   * Create a new notification
   * POST /api/notifications/
   */
  createNotification: async (
    data: NotificationCreate
  ): Promise<Notification> => {
    const response = await apiClient.post<Notification>(
      '/notifications/',
      data
    );
    return response.data;
  },

  /**
   * Mark notification as seen (displayed to user)
   * PATCH /api/notifications/id/{notification_id}/seen
   */
  markAsSeen: async (id: string): Promise<Notification> => {
    const response = await apiClient.patch<Notification>(
      `/notifications/id/${id}/seen`
    );
    return response.data;
  },

  /**
   * Mark notification as read (user clicked/interacted)
   * PATCH /api/notifications/id/{notification_id}/read
   */
  markAsRead: async (id: string): Promise<Notification> => {
    const response = await apiClient.patch<Notification>(
      `/notifications/id/${id}/read`
    );
    return response.data;
  },

  /**
   * Archive a notification
   * PATCH /api/notifications/id/{notification_id}/archive
   */
  archiveNotification: async (id: string): Promise<Notification> => {
    const response = await apiClient.patch<Notification>(
      `/notifications/id/${id}/archive`
    );
    return response.data;
  },

  /**
   * Delete a notification permanently
   * DELETE /api/notifications/id/{notification_id}
   */
  deleteNotification: async (id: string): Promise<void> => {
    await apiClient.delete(`/notifications/id/${id}`);
  },

  // ============================================================================
  // BULK OPERATIONS
  // ============================================================================

  /**
   * Create same notification for multiple users (broadcast)
   * POST /api/notifications/bulk
   */
  createBulkNotifications: async (
    data: BulkNotificationCreate
  ): Promise<Notification[]> => {
    const response = await apiClient.post<Notification[]>(
      '/notifications/bulk',
      data
    );
    return response.data;
  },

  /**
   * Mark multiple notifications as read
   * POST /api/notifications/bulk-read
   */
  bulkMarkAsRead: async (
    data: BulkNotificationOperation
  ): Promise<BulkOperationResponse> => {
    const response = await apiClient.post<BulkOperationResponse>(
      '/notifications/bulk-read',
      data
    );
    return response.data;
  },

  /**
   * Archive multiple notifications
   * POST /api/notifications/bulk-archive
   */
  bulkArchive: async (
    data: BulkNotificationOperation
  ): Promise<BulkOperationResponse> => {
    const response = await apiClient.post<BulkOperationResponse>(
      '/notifications/bulk-archive',
      data
    );
    return response.data;
  },

  /**
   * Delete multiple notifications permanently
   * DELETE /api/notifications/bulk-delete
   */
  bulkDelete: async (
    data: BulkNotificationOperation
  ): Promise<BulkOperationResponse> => {
    const response = await apiClient.delete<BulkOperationResponse>(
      '/notifications/bulk-delete',
      { data }
    );
    return response.data;
  },

  /**
   * Delete all notifications for the current user
   * DELETE /api/notifications/all
   */
  deleteAllNotifications: async (): Promise<BulkOperationResponse> => {
    const response = await apiClient.delete<BulkOperationResponse>(
      '/notifications/all'
    );
    return response.data;
  },

  // ============================================================================
  // SUBSCRIPTIONS
  // ============================================================================

  /**
   * Get all user subscriptions
   * GET /api/notifications/subscriptions/
   */
  getSubscriptions: async (): Promise<Subscription[]> => {
    const response = await apiClient.get<Subscription[]>(
      '/notifications/subscriptions/'
    );
    return response.data;
  },

  /**
   * Get all available subscription types
   * GET /api/notifications/subscriptions/available
   */
  getAvailableSubscriptions: async (): Promise<AvailableSubscriptionsResponse> => {
    const response = await apiClient.get<AvailableSubscriptionsResponse>(
      '/notifications/subscriptions/available'
    );
    return response.data;
  },

  /**
   * Create a new subscription
   * POST /api/notifications/subscriptions/
   */
  createSubscription: async (
    data: SubscriptionCreate
  ): Promise<Subscription> => {
    const response = await apiClient.post<Subscription>(
      '/notifications/subscriptions/',
      data
    );
    return response.data;
  },

  /**
   * Update an existing subscription
   * PUT /api/notifications/subscriptions/id/{subscription_id}
   */
  updateSubscription: async (
    id: string,
    data: SubscriptionUpdate
  ): Promise<Subscription> => {
    const response = await apiClient.put<Subscription>(
      `/notifications/subscriptions/id/${id}`,
      data
    );
    return response.data;
  },

  /**
   * Delete a subscription
   * DELETE /api/notifications/subscriptions/id/{subscription_id}
   */
  deleteSubscription: async (id: string): Promise<void> => {
    await apiClient.delete(`/notifications/subscriptions/id/${id}`);
  },

  /**
   * Reset all subscriptions to default configuration
   * POST /api/notifications/subscriptions/reset-defaults
   */
  resetSubscriptionsToDefaults: async (): Promise<Subscription[]> => {
    const response = await apiClient.post<Subscription[]>(
      '/notifications/subscriptions/reset-defaults'
    );
    return response.data;
  },

  // ============================================================================
  // PREFERENCES
  // ============================================================================

  /**
   * Get all user channel preferences
   * GET /api/notifications/preferences/
   */
  getPreferences: async (): Promise<Preference[]> => {
    const response = await apiClient.get<Preference[]>(
      '/notifications/preferences/'
    );
    return response.data;
  },

  /**
   * Create or update a channel preference (upsert)
   * PUT /api/notifications/preferences/
   */
  updatePreference: async (data: PreferenceCreate): Promise<Preference> => {
    const response = await apiClient.put<Preference>(
      '/notifications/preferences/',
      data
    );
    return response.data;
  },

  // ============================================================================
  // USER OPTIONS
  // ============================================================================

  /**
   * Get user-specific option by key
   * GET /api/notifications/options/{key}
   */
  getUserOption: async (key: string): Promise<UserOption> => {
    const response = await apiClient.get<UserOption>(
      `/notifications/options/${key}`
    );
    return response.data;
  },

  /**
   * Create a new user option
   * POST /api/notifications/options
   */
  createUserOption: async (data: OptionCreate): Promise<UserOption> => {
    const response = await apiClient.post<UserOption>(
      '/notifications/options',
      data
    );
    return response.data;
  },

  /**
   * Update an existing user option
   * PUT /api/notifications/options/{key}
   */
  updateUserOption: async (
    key: string,
    data: OptionUpdate
  ): Promise<UserOption> => {
    const response = await apiClient.put<UserOption>(
      `/notifications/options/${key}`,
      data
    );
    return response.data;
  },

  /**
   * Delete a user option
   * DELETE /api/notifications/options/{key}
   */
  deleteUserOption: async (key: string): Promise<void> => {
    await apiClient.delete(`/notifications/options/${key}`);
  },

  /**
   * Get all user options (aggregated)
   * TODO: This is a mock implementation - backend doesn't have this endpoint yet
   * Should be: GET /api/notifications/options/
   */
  getUserOptions: async (): Promise<{
    sound_enabled: boolean;
    digest_frequency: string;
    quiet_hours_start: string | null;
    quiet_hours_end: string | null;
  }> => {
    // Mock response for now
    return Promise.resolve({
      sound_enabled: true,
      digest_frequency: 'Notifications.digest_frequency.DISABLED',
      quiet_hours_start: null,
      quiet_hours_end: null,
    });

    // Future implementation:
    // const response = await apiClient.get('/notifications/options/');
    // return response.data;
  },

  /**
   * Update all user options (batch update)
   * TODO: This is a mock implementation - backend doesn't have this endpoint yet
   * Should be: PUT /api/notifications/options/
   */
  updateUserOptions: async (data: {
    sound_enabled?: boolean;
    digest_frequency?: string;
    quiet_hours_start?: string | null;
    quiet_hours_end?: string | null;
  }): Promise<void> => {
    // Mock success for now
    console.log('Update user options:', data);
    return Promise.resolve();

    // Future implementation:
    // const response = await apiClient.put('/notifications/options/', data);
    // return response.data;
  },

  // ============================================================================
  // GLOBAL OPTIONS
  // ============================================================================

  /**
   * Get global option by key
   * GET /api/notifications/global-options/{key}
   */
  getGlobalOption: async (key: string): Promise<GlobalOption> => {
    const response = await apiClient.get<GlobalOption>(
      `/notifications/global-options/${key}`
    );
    return response.data;
  },

  /**
   * Create a new global option
   * POST /api/notifications/global-options
   */
  createGlobalOption: async (data: OptionCreate): Promise<GlobalOption> => {
    const response = await apiClient.post<GlobalOption>(
      '/notifications/global-options',
      data
    );
    return response.data;
  },

  /**
   * Update an existing global option
   * PUT /api/notifications/global-options/{key}
   */
  updateGlobalOption: async (
    key: string,
    data: OptionUpdate
  ): Promise<GlobalOption> => {
    const response = await apiClient.put<GlobalOption>(
      `/notifications/global-options/${key}`,
      data
    );
    return response.data;
  },

  /**
   * Delete a global option
   * DELETE /api/notifications/global-options/{key}
   */
  deleteGlobalOption: async (key: string): Promise<void> => {
    await apiClient.delete(`/notifications/global-options/${key}`);
  },

  // ============================================================================
  // WEB PUSH NOTIFICATIONS (TODO: Backend endpoints not yet implemented)
  // ============================================================================

  /**
   * Register push subscription (Web Push)
   * TODO: This endpoint needs to be implemented on the backend
   * Future endpoint: POST /api/notifications/push/subscribe
   */
  registerPushSubscription: async (
    subscription: PushSubscriptionJSON
  ): Promise<{ success: boolean }> => {
    // Backend endpoint not yet implemented
    console.warn('registerPushSubscription: Backend endpoint not implemented');
    console.log('Subscription to register:', subscription);

    // Mock success for now
    return Promise.resolve({ success: true });

    // Future implementation:
    // const response = await apiClient.post('/notifications/push/subscribe', {
    //   subscription,
    // });
    // return response.data;
  },

  /**
   * Unregister push subscription (Web Push)
   * TODO: This endpoint needs to be implemented on the backend
   * Future endpoint: POST /api/notifications/push/unsubscribe
   */
  unregisterPushSubscription: async (): Promise<{ success: boolean }> => {
    // Backend endpoint not yet implemented
    console.warn('unregisterPushSubscription: Backend endpoint not implemented');

    // Mock success for now
    return Promise.resolve({ success: true });

    // Future implementation:
    // const response = await apiClient.post('/notifications/push/unsubscribe');
    // return response.data;
  },

  /**
   * Get VAPID public key from backend
   * TODO: This endpoint needs to be implemented on the backend
   * Future endpoint: GET /api/notifications/push/vapid-public-key
   */
  getVapidPublicKey: async (): Promise<{ public_key: string }> => {
    // Backend endpoint not yet implemented
    console.warn('getVapidPublicKey: Backend endpoint not implemented');

    // Mock key for now (use env variable if available)
    const publicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';
    return Promise.resolve({ public_key: publicKey });

    // Future implementation:
    // const response = await apiClient.get('/notifications/push/vapid-public-key');
    // return response.data;
  },
};
